/**
 * useConnectStreamsInit — thin React wrapper that owns the CCP container DOM
 * element and kicks off `streamsBootstrap.init()` once per page.
 *
 * Auth state, init state, and all SDK event wiring live in `streamsStore` /
 * `streamsBootstrap`. This hook only:
 *   - creates the CCP iframe container (spec-compliant 320×460 off-screen)
 *   - schedules the bootstrap on first paint
 *   - exposes the login-popup helper
 *   - tears down / re-inits CCP on logout and tab demotion
 */
import { useCallback, useEffect, useRef, useSyncExternalStore, type Dispatch, type SetStateAction } from 'react';
import { streamsStore } from '../services/streamsStore';
import { streamsBootstrap } from '../services/streamsBootstrap';
import { ccpLogger } from '../Utils/ccpLogger';
import type { CcpAuthState } from '../types/amazon-connect-streams';

const AWS_CONNECT_CCP_URL = process.env.REACT_APP_AWS_CONNECT_URL || '';
const CCP_CONTAINER_ID = 'aws-connect-ccp-container';

const getPopupConfig = () => ({
    width: 460,
    height: 640,
    left: Math.max(0, Math.round((window.screen.width - 1000) / 2)),
    top: Math.max(0, Math.round((window.screen.height - 640) / 2)),
});

interface UseConnectStreamsInitOptions {
    /** When false, skips CCP container bootstrap entirely (observer tabs). */
    enabled?: boolean;
}

interface UseConnectStreamsInitResult {
    isInitialized: boolean;
    isLoggedIn: boolean;
    ccpAuthState: CcpAuthState;
    ccpContainerRef: React.MutableRefObject<HTMLDivElement | null>;
    openLoginPopup: (onClose?: () => void) => void;
    reauthenticate: () => void;
    /** Deprecated: state is owned by the store. Kept as no-ops for API compat. */
    setIsLoggedIn: Dispatch<SetStateAction<boolean>>;
    setIsInitialized: Dispatch<SetStateAction<boolean>>;
}

function ensureContainer(): HTMLDivElement {
    let el = document.getElementById(CCP_CONTAINER_ID) as HTMLDivElement | null;
    if (!el) {
        el = document.createElement('div');
        el.id = CCP_CONTAINER_ID;
        el.style.cssText =
            'position:fixed;width:320px;height:460px;left:-9999px;top:-9999px;opacity:0;pointer-events:none;';
        document.body.appendChild(el);
        ccpLogger.breadcrumb('Created CCP container (320x460 off-screen)');
    }
    return el;
}

function getBootstrapConfig(container: HTMLElement) {
    return {
        container,
        ccpUrl: AWS_CONNECT_CCP_URL,
        region: 'us-east-1',
    };
}

export const useConnectStreamsInit = (
    options?: UseConnectStreamsInitOptions,
): UseConnectStreamsInitResult => {
    const enabled = options?.enabled ?? true;
    const snapshot = useSyncExternalStore(
        streamsStore.subscribe,
        streamsStore.getSnapshot,
        streamsStore.getSnapshot,
    );

    const ccpContainerRef = useRef<HTMLDivElement | null>(null);
    const loginPopupRef = useRef<Window | null>(null);
    const popupPollIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    // Attach container + kick off bootstrap on first paint (master tab only).
    useEffect(() => {
        if (!enabled) {
            const container = document.getElementById(CCP_CONTAINER_ID);
            if (container?.querySelector('iframe')) {
                ccpLogger.info('Master tab demoted — terminating CCP');
                streamsBootstrap.terminate(container);
            }
            return;
        }

        const container = ensureContainer();
        ccpContainerRef.current = container;

        const rafId = window.requestAnimationFrame(() => {
            void streamsBootstrap.init(getBootstrapConfig(container));
        });

        return () => window.cancelAnimationFrame(rafId);
    }, [enabled]);

    // After explicit logout: terminate + mount a fresh hidden CCP (no page reload).
    useEffect(() => {
        return streamsStore.onEvent((event) => {
            if (event.type !== 'logout' || !event.reinitCcp || !enabledRef.current) return;
            const container = ccpContainerRef.current ?? ensureContainer();
            ccpLogger.info('Re-initializing CCP after logout');
            void streamsBootstrap.reinitAfterLogout(container);
        });
    }, []);

    const closeLoginPopup = useCallback(() => {
        if (popupPollIdRef.current) {
            clearInterval(popupPollIdRef.current);
            popupPollIdRef.current = null;
        }
        const popup = loginPopupRef.current;
        if (popup) {
            try {
                if (!popup.closed) popup.close();
            } catch { /* cross-origin or already closed */ }
            loginPopupRef.current = null;
        }
    }, []);

    // Auto-close popup once the store reports the user is logged in.
    useEffect(() => {
        if (snapshot.isLoggedIn) closeLoginPopup();
    }, [snapshot.isLoggedIn, closeLoginPopup]);

    // Clean up the popup poll on unmount.
    useEffect(() => {
        return () => {
            if (popupPollIdRef.current) clearInterval(popupPollIdRef.current);
        };
    }, []);

    const openLoginPopup = useCallback((onClose?: () => void) => {
        closeLoginPopup();
        streamsStore.onAuthorizing();

        const config = getPopupConfig();
        const features = [
            `width=${config.width}`,
            `height=${config.height}`,
            `left=${config.left}`,
            `top=${config.top}`,
            'menubar=no',
            'toolbar=no',
            'location=yes',
            'status=no',
            'resizable=yes',
            'scrollbars=yes',
        ].join(',');

        const popup = window.open(AWS_CONNECT_CCP_URL, 'AWSConnectLogin', features);
        if (!popup) {
            alert('Please allow popups for this site to log into Amazon Connect');
            return;
        }
        loginPopupRef.current = popup;
        popup.focus();

        // Lightweight popup-closed watcher. Login detection itself is event-driven
        // (connect.agent() fires in the parent via the shared worker).
        popupPollIdRef.current = setInterval(() => {
            if (!popup || popup.closed) {
                ccpLogger.info('Login popup closed');
                if (popupPollIdRef.current) clearInterval(popupPollIdRef.current);
                popupPollIdRef.current = null;
                loginPopupRef.current = null;
                streamsStore.onLoginPopupClosed();
                onClose?.();
            }
        }, 500);
    }, [closeLoginPopup]);

    const reauthenticate = useCallback(() => {
        try {
            streamsStore.onAuthorizing();
            streamsBootstrap.reauthenticate();
        } catch (e) {
            ccpLogger.warn('reauthenticate failed — falling back to login popup', { error: String(e) });
            openLoginPopup();
        }
    }, [openLoginPopup]);

    // Back-compat: consumers destructure these setters from the return value.
    // State is owned by the store now, so the setters are no-ops.
    const noopSetter: Dispatch<SetStateAction<boolean>> = useCallback(() => { /* no-op */ }, []);

    return {
        isInitialized: snapshot.isInitialized,
        isLoggedIn: snapshot.isLoggedIn,
        ccpAuthState: snapshot.ccpAuthState,
        ccpContainerRef,
        openLoginPopup,
        reauthenticate,
        setIsLoggedIn: noopSetter,
        setIsInitialized: noopSetter,
    };
};

export default useConnectStreamsInit;
